import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { sx } from '../../ui/designStyles';

// Small pieces the Home port uses everywhere. The design draws every control as an
// <a href="#"> with inline styles; so does this port (same box model, same pixels), with
// role="button", and a control that must not act (read only, view as member, nothing to
// do) says so with aria-disabled and its reason, and does nothing when pressed.

export function Act({
  css,
  onClick,
  children,
  disabled,
  reason,
  label,
  testId,
  title,
  className,
}: {
  css: string;
  /** "cf-ink": white words stay white in Dark (theme-dark.css). */
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
  /** A write that must not be sent (read only, viewing as a member). */
  disabled?: boolean;
  reason?: string | null;
  label?: string;
  testId?: string;
  title?: string;
}) {
  const off = !!disabled;
  return (
    <a
      href="#"
      role="button"
      className={className}
      style={sx(css)}
      aria-disabled={off || undefined}
      aria-label={label}
      title={off ? reason ?? undefined : title}
      data-testid={testId}
      data-readonly={off ? 'true' : undefined}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        if (!off && onClick) onClick();
      }}
    >
      {children}
    </a>
  );
}

/** A styled span (the design's many one-off spans). */
export function S({ css, children, title, label }: { css: string; children?: ReactNode; title?: string; label?: string }) {
  return (
    <span style={sx(css)} title={title} aria-label={label}>
      {children}
    </span>
  );
}

/** A styled div. */
export function D({ css, children, label, testId, role }: { css: string; children?: ReactNode; label?: string; testId?: string; role?: string }) {
  return (
    <div style={sx(css)} aria-label={label} data-testid={testId} role={role}>
      {children}
    </div>
  );
}

/** The design's chipC(): a 22px tag (MODELED, SAMPLE, CALLED FIRST). */
export const chipC = (fg: string, bg: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:7px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;color:' + fg + ';background:' + bg + ';';

/** The design's operator chip(): a 16px tag. */
export const chip16 = (fg: string, bg: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' + fg + ';background:' + bg + ';';

/** An en dash for a value we do not have (never an em-dash, never a zero). */
export const DASH = '–';

export const style = (css: string): CSSProperties => sx(css);
